import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import UpgradeModal from '../components/UpgradeModal'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import { RsIcon } from '../components/icons'
import axios from 'axios'
import toast from 'react-hot-toast'

const PHASE_LABELS = {
  posted: 'Posted',
  admin_review: 'Review',
  interest: 'Interest',
  due_diligence: 'Due Diligence',
  agreement: 'Agreement',
  escrow: 'Escrow',
  closed: 'Closed',
}

const SELL_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'liquidity', label: 'Need liquidity' },
  { value: 'rebalancing', label: 'Portfolio rebalancing' },
  { value: 'exit', label: 'Exit opportunity' },
  { value: 'other', label: 'Other' },
]

export default function Assets() {
  const { isCompanyPaid, role } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isPortal = PORTAL_ROLES.includes(role)
  const isLpPortal = role === 'LP'

  const [listings, setListings] = useState([])
  const [myListings, setMyListings] = useState([])
  const [tab, setTab] = useState(isLpPortal ? 'sell' : 'browse')
  const [showPaywall, setShowPaywall] = useState(false)
  const [selected, setSelected] = useState(null)
  const [progress, setProgress] = useState(null)
  const [form, setForm] = useState({
    title: '',
    description: '',
    askingPrice: '',
    assetType: 'fund_shares',
    sellReason: '',
    size: '',
    industry: '',
    location: '',
  })

  const load = async () => {
    try {
      const [all, mine] = await Promise.all([
        axios.get('/api/assets'),
        axios.get('/api/assets/my'),
      ])
      setListings(all.data)
      setMyListings(mine.data)
    } catch {
      toast.error('Failed to load asset listings')
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!isCompanyPaid) {
      setShowPaywall(true)
      return
    }
    const payload = {
      title: form.title,
      description: [
        form.size ? `Size: ${form.size}` : '',
        form.sellReason ? `Reason: ${form.sellReason}` : '',
        form.description,
      ]
        .filter(Boolean)
        .join('\n'),
      askingPrice: form.askingPrice,
      assetType: form.assetType === 'fund_shares' ? 'fund' : 'equity',
      industry: form.industry,
      location: form.location,
    }
    try {
      const res = await axios.post('/api/assets', payload)
      toast.success(res.data.message || 'Listing submitted')
      setForm({
        title: '',
        description: '',
        askingPrice: '',
        assetType: 'fund_shares',
        sellReason: '',
        size: '',
        industry: '',
        location: '',
      })
      if (isLpPortal) setTab('mine')
      load()
    } catch (err) {
      if (err.response?.status === 402 || err.response?.data?.code === 'COMPANY_PAYWALL') {
        setShowPaywall(true)
        return
      }
      toast.error(err.response?.data?.message || 'Failed to post')
    }
  }

  const openDetail = async (asset) => {
    setSelected(asset)
    try {
      const res = await axios.get(`/api/assets/${asset.id}/progress`)
      setProgress(res.data)
    } catch {
      setProgress(null)
    }
  }

  useEffect(() => {
    const selectedId = searchParams.get('selected')
    if (!selectedId) return
    const openFromUrl = async () => {
      try {
        const res = await axios.get(`/api/assets/${selectedId}`)
        await openDetail(res.data)
        setTab('browse')
      } catch {
        toast.error('Could not open asset detail')
      }
    }
    openFromUrl()
  }, [searchParams.get('selected')])

  const contactSeller = async (asset) => {
    try {
      const res = await axios.post(`/api/assets/${asset.id}/contact-seller`)
      navigate(`/chat?contact=${res.data.sellerId}`)
    } catch (err) {
      if (err.response?.status === 402) {
        setShowPaywall(true)
        return
      }
      toast.error(err.response?.data?.message || 'Cannot contact seller')
    }
  }

  const displayList = tab === 'mine' ? myListings : listings

  const tabs = isLpPortal
    ? [
        { id: 'sell', label: 'Submit Listing' },
        { id: 'mine', label: 'My Listings' },
        { id: 'browse', label: 'Browse Market' },
      ]
    : [
        { id: 'browse', label: 'Browse Market' },
        { id: 'mine', label: 'My Listings' },
      ]

  const renderList = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {displayList.map((a) => (
          <div
            key={a.id}
            className={
              isPortal
                ? 'portal-asset-list-card'
                : 'card p-5 cursor-pointer hover:shadow-md transition-shadow'
            }
            onClick={() => openDetail(a)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && openDetail(a)}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-navy">{a.title}</h3>
                <p className="text-sm text-gray-500">
                  {a.seller_name} · {PHASE_LABELS[a.phase] || a.phase}
                </p>
              </div>
              <span className="text-sm font-semibold text-primary-600">
                {a.asking_price ? `¥${Number(a.asking_price).toLocaleString()}` : 'TBD'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{a.description}</p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  contactSeller(a)
                }}
                className="text-sm text-primary-600 flex items-center gap-1"
              >
                <RsIcon name="messageSquare" size={16} className="w-4 h-4" /> Contact Seller
              </button>
            </div>
          </div>
        ))}
        {!displayList.length && (
          <p className="text-gray-400 text-center py-12">No listings yet</p>
        )}
      </div>

      {selected && (
        <div className={`${isPortal ? 'portal-form-card' : 'card p-5'} h-fit sticky top-8`}>
          <h3 className="font-bold text-navy mb-2">{selected.title}</h3>
          {progress && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <RsIcon name="trendUp" size={16} className="w-4 h-4 text-primary-500" />
                <span className="text-sm">Progress {progress.progress}%</span>
              </div>
              <div className="space-y-2">
                {progress.phases?.map((p) => (
                  <div key={p.key} className="flex items-center gap-2 text-xs">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        p.status === 'completed'
                          ? 'bg-green-500'
                          : p.status === 'active'
                            ? 'bg-primary-500'
                            : 'bg-gray-300'
                      }`}
                    />
                    <span>{PHASE_LABELS[p.key] || p.key}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4">{progress.offlineAgreement?.note}</p>
            </>
          )}
          <button
            type="button"
            onClick={() => contactSeller(selected)}
            className={`w-full mt-4 text-sm py-2 ${isPortal ? 'portal-btn-primary' : 'btn-primary'}`}
          >
            Contact Seller
          </button>
        </div>
      )}
    </div>
  )

  const renderSellForm = () => (
    <form onSubmit={handleCreate} className="portal-form-card">
      <div className="portal-sell-info">
        <p>
          <strong>Note:</strong> As an LP, you can sell existing fund shares or specific project equity
          that you&apos;ve already invested in. This is NOT for raising new funds.
        </p>
      </div>
      <div className="portal-form-group">
        <label>What are you selling? *</label>
        <div className="portal-radio-group">
          <label className="portal-radio-item">
            <input
              type="radio"
              name="sell-type"
              checked={form.assetType === 'fund_shares'}
              onChange={() => setForm((f) => ({ ...f, assetType: 'fund_shares' }))}
            />
            Fund Shares
          </label>
          <label className="portal-radio-item">
            <input
              type="radio"
              name="sell-type"
              checked={form.assetType === 'project_equity'}
              onChange={() => setForm((f) => ({ ...f, assetType: 'project_equity' }))}
            />
            Project Equity
          </label>
        </div>
      </div>
      <div className="portal-form-group">
        <label htmlFor="asset-name">Fund / Project Name *</label>
        <input
          id="asset-name"
          type="text"
          placeholder="e.g. ABC Fund II, Project XYZ"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          required
        />
      </div>
      <div className="portal-form-group">
        <label htmlFor="size">Share / Equity Size *</label>
        <input
          id="size"
          type="text"
          placeholder="e.g. 15% of fund, 100,000 shares"
          value={form.size}
          onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))}
          required
        />
      </div>
      <div className="portal-form-group">
        <label htmlFor="price">Expected Price / Valuation</label>
        <input
          id="price"
          type="text"
          placeholder="e.g. $5M, 2x original investment"
          value={form.askingPrice}
          onChange={(e) => setForm((f) => ({ ...f, askingPrice: e.target.value }))}
        />
      </div>
      <div className="portal-form-group">
        <label htmlFor="reason">Reason for Selling</label>
        <select
          id="reason"
          value={form.sellReason}
          onChange={(e) => setForm((f) => ({ ...f, sellReason: e.target.value }))}
        >
          {SELL_REASONS.map((r) => (
            <option key={r.value || 'empty'} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <div className="portal-form-group">
        <label htmlFor="description">Additional Details</label>
        <textarea
          id="description"
          placeholder="Provide more details about the shares/equity being sold..."
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </div>
      {!isCompanyPaid && (
        <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded mb-4">
          Company subscription required to publish portfolio listings. Personal paid plans cannot post listings.
        </p>
      )}
      <button type="submit" className="portal-btn-primary" disabled={!isCompanyPaid}>
        Submit Listing
      </button>
    </form>
  )

  return (
    <PageShell>
      <UpgradeModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        onUpgrade={() => {
          setShowPaywall(false)
          navigate('/payment')
        }}
        message="Selling your portfolio requires an active company subscription. Personal paid accounts cannot publish listings."
      />

      {isPortal ? (
        <>
          <PortalHeader
            title={isLpPortal ? 'Sell my Portfolio' : 'Assets'}
            showSearch={false}
          />
          {isLpPortal && tab === 'sell' && (
            <div className="portal-sell-header mb-6">
              <p>Offer your fund shares or project equity to potential buyers</p>
              <button
                type="button"
                className="portal-card-action"
                style={{ marginTop: 8, display: 'inline-block' }}
                onClick={() => setTab('mine')}
              >
                View my listings →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy">Asset Sales</h1>
            <p className="text-sm text-gray-500 mt-1">
              Post listings; buyers need a paid plan to contact you.
            </p>
          </div>
          {!isLpPortal && (
            <button type="button" onClick={() => setTab('sell')} className="btn-primary flex items-center gap-2">
              <RsIcon name="plus" size={16} className="w-4 h-4" /> Post Asset
            </button>
          )}
        </div>
      )}

      {!(isPortal && isLpPortal && tab === 'sell') && (
      <div className={isPortal ? 'portal-filter-tabs mb-6' : 'flex gap-2 mb-6'}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              isPortal
                ? `portal-filter-tab${tab === t.id ? ' active' : ''}`
                : `px-4 py-2 rounded-lg text-sm font-medium ${
                    tab === t.id ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 border'
                  }`
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      )}

      {tab === 'sell' && isLpPortal ? (
        <div className="portal-page-full">{renderSellForm()}</div>
      ) : renderList()}
    </PageShell>
  )
}
